/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useState, forwardRef, MutableRefObject } from 'react'
import { trackEvent } from 'analytics/trackFunctions'
import { NumericFormat } from 'react-number-format'

import { THOUSAND } from 'constants/credit'
import { nonNumericalChars } from 'utils'

const hasOnlyNumbers = (val) => /^\d*$/.test(val)

interface AmountInputProps {
  minAmount: number
  maxAmount: number
  value: string
  setValue: React.Dispatch<React.SetStateAction<string>>
  divider?: number
  isCashout?: boolean
}

const inputClasses =
  'w-full grid transition-all duration-300 grid-cols-[min-content_min-content_min-content] bg-white rounded text-center align-middle justify-center items-center py-[1.125rem] outline outline-2 outline-gray-500 px-3 font-bold text-4xl border-gray-500 focus-within:outline-purple-900 focus-within:outline focus-within:outline-2 focus-within:ring-gray-300 focus-within:ring-4 focus-within:ring-offset-1 disabled:text-gray-500'

const AmountInput = forwardRef(
  (
    {
      minAmount,
      maxAmount,
      value,
      setValue,
      divider = THOUSAND,
      isCashout = false
    }: AmountInputProps,
    ref: MutableRefObject<HTMLInputElement>
  ) => {
    const [error, setError] = useState(false)
    const [errorText, setErrorText] = useState('')

    const isLessThanAllowed = (number) => Number(number) * divider < minAmount
    const isMoreThanAllowed = (number) => Number(number) * divider > maxAmount

    function handleOnChange(newValue) {
      if (!hasOnlyNumbers(newValue)) return
      setError(false)

      if (
        isLessThanAllowed(newValue) ||
        (isCashout && Number(newValue) === 0)
      ) {
        setError(true)
        setErrorText('Ingresá un monto mayor')
      }

      if (isMoreThanAllowed(newValue)) {
        setError(true)
        setErrorText('Ingresá un monto menor')
      }

      if (isCashout && maxAmount == 0 && newValue > 0) {
        setError(true)
        setErrorText('Saldo insuficiente')
      }

      const noLeadingZeroes = newValue.replace(/^0+/, '')

      setValue(noLeadingZeroes)
    }

    function handleOnBlur() {
      if (!error) return

      if (isLessThanAllowed(value)) {
        setValue((minAmount / divider).toString())
      }

      if (isMoreThanAllowed(value)) {
        setValue((maxAmount / divider).toString())
      }

      if (isCashout) {
        if (maxAmount == 0 && Number(value) > 0) {
          setError(true)
          setErrorText('Saldo insuficiente')
          return
        }

        if (isLessThanAllowed(value) || Number(value) === 0) {
          setError(true)
          setErrorText('Ingresá un monto mayor')
          return
        }
      }

      setError(false)
    }

    // 🧐: this function is to keep in mind Manrope is a proportional font. each character vary in width being in this particular font size (2 rem | 32px) between 14px and 21px. so using only a unit like "ch" to resize when value contains a narrow char like "1" results in a weird blank space. when there is a "1" in the string, this function transforms the returned unit to pixels
    function resizeInput() {
      if (value.length > 0) {
        // checks how many times char "1" exists
        const timesChar1 = value.replace(/[^1]/g, '').length
        // checks how many other chars exists
        const rest = value.length - timesChar1
        // return the exact width of the "1"s plus other chars

        const separators = Math.floor(value.length / 4)
        const finalWidth = timesChar1 * 13 + rest * 21 + separators * 12

        if (isCashout) return `${finalWidth + 6}px`

        return `${finalWidth}px`
      }
      return '21px'
    }

    return (
      <>
        <div
          className={`${inputClasses} ${
            !!error && '!outline !outline-2 !outline-red-900'
          }`}
          onClick={() => {
            trackEvent({ eventName: 'nuevo_renov_oferta_input_texto' })
            ref.current.focus()
          }}
        >
          <span className='mr-2'>$</span>
          <NumericFormat
            className='p-0 focus-visible:outline-none font-bold transition-all ease-out duration-100 focus:outline-none border-transparent focus:border-transparent focus:ring-0 peer !max-w-fit text-4xl'
            style={{
              width: resizeInput()
            }}
            onKeyDown={(e) => {
              if (nonNumericalChars.includes(e.key)) e.preventDefault()
            }}
            getInputRef={ref}
            disabled={maxAmount === 0}
            maxLength={isCashout ? 9 : 4}
            onBlur={() => handleOnBlur()}
            value={value}
            id='amount'
            autoComplete='off'
            onValueChange={(values) => handleOnChange(values.value)}
            thousandSeparator='.'
            decimalSeparator=','
          />
          {isCashout ? null : (
            <span className='peer-focus:text-gray-900 pt-[1px] -ml-[1px]'>
              .000
            </span>
          )}
        </div>
        {error ? (
          <div className='w-full flex justify-center mt-2 font-bold text-2xs text-red-900'>
            {errorText}
          </div>
        ) : (
          <div className='w-full flex justify-center mt-2'>
            <label
              className='font-bold text-2xs text-gray-900'
              htmlFor='amount'
            >
              Ingresá un monto
            </label>
          </div>
        )}
      </>
    )
  }
)

AmountInput.displayName = 'AmountInput'
export default AmountInput
